import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import Config from "../Config/Config";

// -------- Base URLs --------
const baseURL = (Config.baseUrl || "").replace(/\/+$/, "");     // e.g. http://localhost:8000/api
const REFRESH_URL = `${baseURL}/refreshtoken`;

// -------- Axios (credentialed) --------
/**
 * Axios instance configured for API requests.
 *
 * @constant
 * @type {import('axios').AxiosInstance}
 * @property {string} baseURL - The base URL for all API requests.
 * @property {number} timeout - Request timeout in milliseconds (60 seconds).
 * @property {boolean} withCredentials - Indicates whether or not cross-site Access-Control requests should be made using credentials.
 * @property {string} credentials - Indicates whether credentials such as cookies or authorization headers should be sent with requests ("include").
 * @property {Object} headers - Default headers for all requests, including "Content-Type" and "Accept" set to "application/json".
 */
const axiosInstance = axios.create({
  baseURL,
  timeout: 60_000,
  withCredentials: true,
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// -------- CSRF helper --------
function readCookie(name) {
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([$^*+?.()|[\]{}\\])/g, "\\$1") + "=([^;]*)")
  );
  return m ? decodeURIComponent(m[1]) : "";
}

// Attach X-CSRF-Token (double-submit) for all requests
axiosInstance.interceptors.request.use((config) => {
  const csrf = readCookie("csrf"); // must match CSRF_COOKIE_NAME on server
  if (csrf) {
    config.headers = config.headers ?? {};
    (config.headers)["X-CSRF-Token"] = csrf;
  }
  return config;
});

// -------- Refresh queue (avoid stampede) --------
let isRefreshing = false;
let waiters = [];

function enqueueWaiter(cb) {
  waiters.push(cb);
}
function resolveWaiters() {
  waiters.forEach((cb) => cb(true));
  waiters = [];
}
function rejectWaiters(err) {
  waiters.forEach((cb) => cb(null, err));
  waiters = [];
}

async function performRefresh() {
  // Call refresh endpoint; cookies carry the RT; server rotates cookies
  return axios.post(
    REFRESH_URL,
    {},
    {
      withCredentials: true,
      timeout: 30_000,
      headers: { "X-CSRF-Token": readCookie("csrf") || "" }, // usually skipped by server for /refreshtoken
    }
  );
}

// Response interceptor: on 401, try one refresh then retry original
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const originalUrl = typeof original?.url === "string" ? original.url : "";
    const isAuthEndpoint =
      originalUrl.includes("/signin") ||
      originalUrl.includes("/refreshtoken") ||
      originalUrl.includes("/logout");

    if (!original || status !== 401 || original._retry || isAuthEndpoint) {
      // Non-401 or already retried → bubble up
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      // Wait for in-flight refresh
      return new Promise((resolve, reject) => {
        enqueueWaiter((ok, err) => {
          if (!ok) return reject(err || new Error("refresh failed"));
          resolve(axiosInstance(original));
        });
      });
    }

    // Start refresh
    isRefreshing = true;
    try {
      await performRefresh();
      isRefreshing = false;
      resolveWaiters();

      // Retry original request (cookies now have fresh AT)
      return axiosInstance(original);
    } catch (refreshErr) {
      isRefreshing = false;
      rejectWaiters(refreshErr);

      const issueType = refreshErr?.response?.data?.issueType;
      // Hard redirect when the session can no longer be refreshed.
      if (issueType === "password_expired") {
        if (window.location.pathname !== "/Forgot") {
          window.location.replace("/Forgot?reason=password_expired");
        }
      } else if (window.location.pathname !== "/Login") {
        window.location.replace("/Login");
      }
      return Promise.reject(refreshErr);
    }
  }
);

// -------- API Service wrapper --------
export default class APIService {
  addFileDataToBackend = async (data, methodName, config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.post(url, data, config);
  };

  addDataToBackend = async (data, methodName, _config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.post(url, data);
  };

  updateDataToBackend = async (data, methodName, _config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.post(url, data);
  };

  patchDataToBackend = async (data, methodName, config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.patch(url, data, config);
  };

  deleteDataFromBackend = async (methodName, config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.delete(url, config);
  };

  getGeneratedFile = async (data, methodName, _config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.post(url, data, { responseType: "blob" });
  };

  getDataFromBackend = async (methodName, _config) => {
    const url = `${baseURL}/${methodName.replace(/^\/+/, "")}`;
    return axiosInstance.get(url);
  };

  downloadIvrSecureFilesZip = async (ivrId) => {
    const url = `${baseURL}/ivr/${encodeURIComponent(ivrId)}/files.zip`;
    return axiosInstance.get(url, { responseType: "arraybuffer" });
  };

  downloadIvrSecureFile = async (ivrId, type) => {
    const url = `${baseURL}/ivr/${encodeURIComponent(ivrId)}/files/${encodeURIComponent(type)}`;
    return axiosInstance.get(url, { responseType: "blob" });
  };

  getOrderOptions = async () => {
    const url = `${baseURL}/getdooptions`;
    return axiosInstance.get(url);
  };

  // ---- Azure SAS helpers (use plain axios; no app cookies needed) ----
  getSasToken = async (fileName) => {
    // const baseURL = "https://api.tissuevault.health/api";
    const url = `${baseURL}/getsastoken?filename=${encodeURIComponent(fileName)}`;
    console.log("Requesting SAS token for:", url);
    const res = await axiosInstance.get(url);
    return res?.data?.data;
  };

  checkBlobExists = async (sasUrl) => {
    try {
      const response = await axios.head(sasUrl); // plain axios, no withCredentials
      return response.status === 200;
    } catch (error) {
      if (error?.response?.status === 404) return false;
      return false;
    }
  };

  fetchFileAsArrayBuffer = async (sasUrl) => {
    const response = await axios.get(sasUrl, { responseType: "arraybuffer" });
    if (response.status === 200) return response.data;
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  };
}
